import { z } from "zod";
import { IdSchema, IsoDateTimeSchema, pageSchema } from "./common.js";

// ---------------------------------------------------------------------------
// Provider / Model
// ---------------------------------------------------------------------------

export const ProviderKindSchema = z.enum(["LLM_API", "SUBSCRIPTION_CLI", "TOOL", "FAKE"]);
export type ProviderKind = z.infer<typeof ProviderKindSchema>;

export const AuthMethodSchema = z.enum(["API_KEY", "OAUTH", "CLI_SESSION", "NONE"]);
export type AuthMethod = z.infer<typeof AuthMethodSchema>;

export const AccountTypeSchema = z.enum([
	"INDIVIDUAL_API",
	"INDIVIDUAL_SUBSCRIPTION",
	"TEAM",
	"ENTERPRISE",
	"FAKE",
]);
export type AccountType = z.infer<typeof AccountTypeSchema>;

export const ProviderSchema = z.object({
	id: IdSchema,
	key: z.string().min(1),
	displayName: z.string().min(1),
	kind: ProviderKindSchema,
	status: z.enum(["ACTIVE", "MAINTENANCE", "DISABLED"]),
	/** Adapter implementation selected at runtime. `fake` always works offline. */
	adapter: z.enum(["fake", "openai", "anthropic"]),
	priority: z.number().int(),
	createdAt: IsoDateTimeSchema,
});
export type Provider = z.infer<typeof ProviderSchema>;

export const ModelSchema = z.object({
	id: IdSchema,
	providerId: IdSchema,
	key: z.string().min(1),
	displayName: z.string().min(1),
	capabilities: z.array(z.string()),
	contextWindow: z.number().int().min(0),
	inputCostPerMTokens: z.number().min(0),
	outputCostPerMTokens: z.number().min(0),
	status: z.enum(["ACTIVE", "DEPRECATED", "DISABLED"]),
	createdAt: IsoDateTimeSchema,
});
export type Model = z.infer<typeof ModelSchema>;

export const CreateProviderRequestSchema = ProviderSchema.pick({
	key: true,
	displayName: true,
	kind: true,
	adapter: true,
}).extend({
	priority: z.number().int().default(100),
});
export type CreateProviderRequest = z.infer<typeof CreateProviderRequestSchema>;

export const CreateModelRequestSchema = ModelSchema.pick({
	key: true,
	displayName: true,
	capabilities: true,
}).extend({
	contextWindow: z.number().int().min(0).default(128000),
	inputCostPerMTokens: z.number().min(0).default(0),
	outputCostPerMTokens: z.number().min(0).default(0),
});
export type CreateModelRequest = z.infer<typeof CreateModelRequestSchema>;

// ---------------------------------------------------------------------------
// AuthenticationProfile — design §21.5
// ---------------------------------------------------------------------------

export const OwnerTypeSchema = z.enum(["USER", "TENANT", "PLATFORM"]);
export type OwnerType = z.infer<typeof OwnerTypeSchema>;

export const CredentialClassSchema = z.enum([
	"USER_SUBSCRIPTION",
	"USER_API",
	"SYSTEM_SUBSCRIPTION",
	"SYSTEM_API",
	"TENANT_API",
]);
export type CredentialClass = z.infer<typeof CredentialClassSchema>;

/**
 * A Secret Reference. The platform NEVER stores secret material; it stores a
 * pointer that a vault adapter resolves at call time.
 * Supported forms: `env:NAME`, `vault:path/to/secret`, `fake:label`.
 */
export const SecretRefSchema = z
	.string()
	.min(3)
	.max(200)
	.regex(/^(env|vault|fake):[A-Za-z0-9_\-./]+$/, "must be env:NAME, vault:PATH or fake:LABEL");
export type SecretRef = z.infer<typeof SecretRefSchema>;

export const AuthenticationProfileSchema = z.object({
	id: IdSchema,
	providerId: IdSchema,
	authMethod: AuthMethodSchema,
	accountType: AccountTypeSchema,
	ownerType: OwnerTypeSchema,
	ownerId: IdSchema,
	/** Alias shown to end users. Never the real account/login id. */
	displayName: z.string().min(1),
	credentialClass: CredentialClassSchema,
	secretRef: SecretRefSchema,
	status: z.enum(["ACTIVE", "DISABLED", "EXPIRED", "UNVERIFIED"]),
	providerAuthPolicyId: IdSchema.nullable(),
	verifiedAt: IsoDateTimeSchema.nullable(),
	expiresAt: IsoDateTimeSchema.nullable(),
	createdAt: IsoDateTimeSchema,
});
export type AuthenticationProfile = z.infer<typeof AuthenticationProfileSchema>;

export const CreateAuthenticationProfileRequestSchema = AuthenticationProfileSchema.pick({
	providerId: true,
	authMethod: true,
	accountType: true,
	ownerType: true,
	ownerId: true,
	displayName: true,
	credentialClass: true,
	secretRef: true,
});
export type CreateAuthenticationProfileRequest = z.infer<
	typeof CreateAuthenticationProfileRequestSchema
>;

// ---------------------------------------------------------------------------
// ProviderAuthPolicy — design §21.6 (hard limit)
// ---------------------------------------------------------------------------

export const ShareScopeSchema = z.enum(["NONE", "SINGLE_USER", "TENANT", "PLATFORM"]);
export type ShareScope = z.infer<typeof ShareScopeSchema>;

export const ProviderAuthPolicySchema = z.object({
	id: IdSchema,
	providerId: IdSchema,
	authMethod: AuthMethodSchema,
	accountType: AccountTypeSchema,
	credentialOwnerType: OwnerTypeSchema,
	allowedShareScope: ShareScopeSchema,
	multiUserAllowed: z.boolean(),
	multiTenantAllowed: z.boolean(),
	allowedPurposes: z.array(z.string()),
	commercialUseAllowed: z.boolean(),
	runtimeInjectionAllowed: z.boolean(),
	maxConcurrency: z.number().int().min(0),
	/** UNVERIFIED is never treated as allowed. */
	policyStatus: z.enum(["ALLOWED", "DENIED", "UNVERIFIED"]),
	termsReference: z.string().nullable(),
	verifiedAt: IsoDateTimeSchema.nullable(),
	createdAt: IsoDateTimeSchema,
});
export type ProviderAuthPolicy = z.infer<typeof ProviderAuthPolicySchema>;

export const CreateProviderAuthPolicyRequestSchema = ProviderAuthPolicySchema.omit({
	id: true,
	createdAt: true,
	verifiedAt: true,
}).partial({
	allowedPurposes: true,
	termsReference: true,
});
export type CreateProviderAuthPolicyRequest = z.infer<typeof CreateProviderAuthPolicyRequestSchema>;

// ---------------------------------------------------------------------------
// SystemAuthPool / Assignment — design §21.7
// ---------------------------------------------------------------------------

export const SystemAuthPoolSchema = z.object({
	id: IdSchema,
	providerId: IdSchema,
	authMethod: AuthMethodSchema,
	accountType: AccountTypeSchema,
	/** The only name end users ever see. */
	displayAlias: z.string().min(1),
	purposeScope: z.array(z.string()),
	providerAuthPolicyId: IdSchema.nullable(),
	selectionStrategy: z.enum(["PRIORITY", "ROUND_ROBIN", "LEAST_USED"]),
	status: z.enum(["ACTIVE", "DISABLED"]),
	maxConcurrency: z.number().int().min(0),
	createdAt: IsoDateTimeSchema,
});
export type SystemAuthPool = z.infer<typeof SystemAuthPoolSchema>;

export const SystemAuthPoolMemberSchema = z.object({
	id: IdSchema,
	poolId: IdSchema,
	authenticationProfileId: IdSchema,
	memberPriority: z.number().int(),
	weight: z.number().int().min(0),
	status: z.enum(["ACTIVE", "DISABLED"]),
});
export type SystemAuthPoolMember = z.infer<typeof SystemAuthPoolMemberSchema>;

const SystemAuthAssignmentBaseSchema = z.object({
	id: IdSchema,
	targetType: z.enum(["ACCOUNT", "POOL"]),
	targetId: IdSchema,
	subjectType: z.enum(["USER", "GROUP", "ROLE", "TENANT", "PROJECT", "ANY_TENANT"]),
	subjectId: IdSchema,
	/** DENY always wins over ALLOW. */
	effect: z.enum(["ALLOW", "DENY"]),
	allowedPurposes: z.array(z.string()),
	quotaOverride: z.number().int().min(0).nullable(),
	concurrencyOverride: z.number().int().min(0).nullable(),
	validFrom: IsoDateTimeSchema.nullable(),
	validUntil: IsoDateTimeSchema.nullable(),
	createdAt: IsoDateTimeSchema,
});
export const SystemAuthAssignmentSchema = SystemAuthAssignmentBaseSchema.superRefine(
	(value, ctx) => {
		if (value.subjectType === "ANY_TENANT" && value.subjectId !== "*") {
			ctx.addIssue({
				code: "custom",
				path: ["subjectId"],
				message: 'ANY_TENANT assignments must use subjectId "*"',
			});
		}
	},
);
export type SystemAuthAssignment = z.infer<typeof SystemAuthAssignmentSchema>;

export const CreateSystemAuthAssignmentRequestSchema = SystemAuthAssignmentBaseSchema.omit({
	id: true,
	createdAt: true,
})
	.partial({
		allowedPurposes: true,
		quotaOverride: true,
		concurrencyOverride: true,
		validFrom: true,
		validUntil: true,
	})
	.superRefine((value, ctx) => {
		if (value.subjectType === "ANY_TENANT" && value.subjectId !== "*") {
			ctx.addIssue({
				code: "custom",
				path: ["subjectId"],
				message: 'ANY_TENANT assignments must use subjectId "*"',
			});
		}
	});
export type CreateSystemAuthAssignmentRequest = z.infer<
	typeof CreateSystemAuthAssignmentRequestSchema
>;

// ---------------------------------------------------------------------------
// Connection test
// ---------------------------------------------------------------------------

export const ConnectionTestResultSchema = z.object({
	authenticationProfileId: IdSchema,
	ok: z.boolean(),
	/** Never contains secret material. */
	message: z.string(),
	reason: z.string().nullable(),
	adapter: z.string(),
	latencyMs: z.number().int().min(0),
	accountMetadata: z.record(z.string(), z.string()),
	testedAt: IsoDateTimeSchema,
});
export type ConnectionTestResult = z.infer<typeof ConnectionTestResultSchema>;

// ---------------------------------------------------------------------------
// Task profile / routing — design §21.11-21.14
// ---------------------------------------------------------------------------

export const TaskTypeSchema = z.enum([
	"ASSIST",
	"INTERVIEW",
	"PLANNING",
	"RESEARCH",
	"REASONING",
	"CODING",
	"CODE_REVIEW",
	"TEST_ANALYSIS",
	"DESIGN",
	"DOCUMENT",
	"DATA_ANALYSIS",
	"IMAGE_GENERATION",
	"TOOL_USE",
	"DEPLOY",
	"MONITORING",
]);
export type TaskType = z.infer<typeof TaskTypeSchema>;

export const RouteConstraintModeSchema = z.enum(["PREFERRED", "REQUIRED"]);
export type RouteConstraintMode = z.infer<typeof RouteConstraintModeSchema>;

export const TaskProfileSchema = z.object({
	taskType: TaskTypeSchema,
	/** UCS/Vertical namespaced subtype, e.g. `web.react_implementation`. */
	taskSubtype: z.string().nullable().default(null),
	requiredCapabilities: z.array(z.string()).default([]),
	dataClassification: z.enum(["PUBLIC", "INTERNAL", "CONFIDENTIAL"]).default("INTERNAL"),
	routeConstraintMode: RouteConstraintModeSchema.default("PREFERRED"),
	preferredRouteKeys: z.array(z.string()).default([]),
	requiredRouteKeys: z.array(z.string()).default([]),
});
export type TaskProfile = z.infer<typeof TaskProfileSchema>;

export const RouteCandidateEvaluationSchema = z.object({
	authenticationProfileId: IdSchema,
	displayName: z.string(),
	credentialClass: CredentialClassSchema,
	rank: z.number().int().min(0),
	selected: z.boolean(),
	/** Why this candidate was excluded. Null when selected. */
	excludedReason: z.string().nullable(),
});
export type RouteCandidateEvaluation = z.infer<typeof RouteCandidateEvaluationSchema>;

/** Design §21.13 — reproducible decision trace, never contains secrets. */
export const RoutingDecisionSchema = z.object({
	decisionId: IdSchema,
	sessionId: IdSchema.nullable(),
	taskProfile: TaskProfileSchema,
	taskProfileHash: z.string(),
	routingRulesVersion: z.string(),
	selectedRouteKey: z.string().nullable(),
	selectedProviderId: IdSchema.nullable(),
	selectedModelId: IdSchema.nullable(),
	selectedAuthenticationProfileId: IdSchema.nullable(),
	/** Set when a system pool was used; the physical account stays internal. */
	selectedPoolId: IdSchema.nullable(),
	selectedPoolAlias: z.string().nullable(),
	constraintMode: RouteConstraintModeSchema,
	outcome: z.enum([
		"SELECTED",
		"FALLBACK_AUTO",
		"FALLBACK_APPROVAL_REQUIRED",
		"FALLBACK_DENIED",
		"NO_CANDIDATE",
	]),
	reason: z.string(),
	/**
	 * True when every candidate was excluded for a reason a person can fix by
	 * re-authenticating or waiting for quota — as opposed to a policy denial,
	 * which re-authentication will not change.
	 */
	recoverableByCredentialRefresh: z.boolean().default(false),
	excludedRoutes: z.array(z.object({ routeKey: z.string(), reason: z.string() })),
	candidates: z.array(RouteCandidateEvaluationSchema),
	decidedAt: IsoDateTimeSchema,
	traceId: z.string(),
});
export type RoutingDecision = z.infer<typeof RoutingDecisionSchema>;

export const ResolveRouteRequestSchema = z.object({
	sessionId: IdSchema.nullable().default(null),
	tenantId: IdSchema,
	userId: IdSchema,
	taskProfile: TaskProfileSchema,
	/** Simulated outages, used by the failure-path scenarios. */
	simulate: z
		.object({
			unavailableProviderKeys: z.array(z.string()).default([]),
			exhaustedAuthProfileIds: z.array(IdSchema).default([]),
		})
		.default({ unavailableProviderKeys: [], exhaustedAuthProfileIds: [] }),
});
export type ResolveRouteRequest = z.infer<typeof ResolveRouteRequestSchema>;

// ---------------------------------------------------------------------------
// Provider invocation — design §20.6
// ---------------------------------------------------------------------------

/** Canonical Model.capabilities tokens (extend carefully; callers gate on these). */
export const ModelCapabilitySchema = z.enum(["text", "vision", "web_search", "tools"]);
export type ModelCapability = z.infer<typeof ModelCapabilitySchema>;

export const ContentPartSchema = z.discriminatedUnion("type", [
	z.object({
		type: z.literal("text"),
		text: z.string().min(1),
	}),
	z.object({
		type: z.literal("image"),
		artifactId: IdSchema.nullable().default(null),
		url: z.string().url().nullable().default(null),
		mimeType: z.string().min(1).default("image/png"),
	}),
	z.object({
		type: z.literal("file"),
		artifactId: IdSchema,
		name: z.string().min(1),
		mimeType: z.string().min(1),
	}),
]);
export type ContentPart = z.infer<typeof ContentPartSchema>;

export const InvokeProviderToolsSchema = z.object({
	webSearch: z.boolean().default(false),
});
export type InvokeProviderTools = z.infer<typeof InvokeProviderToolsSchema>;

export const ProviderInvocationSchema = z.object({
	id: IdSchema,
	tenantId: IdSchema,
	executionId: IdSchema.nullable(),
	nodeExecutionId: IdSchema.nullable(),
	nodeAttemptId: IdSchema.nullable(),
	sessionId: IdSchema.nullable(),
	providerId: IdSchema,
	modelId: IdSchema.nullable(),
	authenticationProfileId: IdSchema.nullable(),
	routeRank: z.number().int().min(0),
	routingDecisionId: IdSchema.nullable(),
	requestHash: z.string(),
	providerRequestId: z.string().nullable(),
	inputTokens: z.number().int().min(0),
	outputTokens: z.number().int().min(0),
	cachedTokens: z.number().int().min(0),
	estimatedCost: z.number().min(0),
	actualCost: z.number().min(0).nullable(),
	currency: z.string(),
	meteringMode: z.enum([
		"ACTUAL_MONEY",
		"ACTUAL_USAGE",
		"ESTIMATED_OPPORTUNITY_COST",
		"UNAVAILABLE",
	]),
	outcome: z.enum(["SUCCEEDED", "FAILED", "TIMED_OUT", "CANCELLED"]),
	errorClass: z.string().nullable(),
	startedAt: IsoDateTimeSchema,
	endedAt: IsoDateTimeSchema.nullable(),
});
export type ProviderInvocation = z.infer<typeof ProviderInvocationSchema>;

export const InvokeProviderRequestSchema = z
	.object({
		tenantId: IdSchema,
		userId: IdSchema,
		sessionId: IdSchema.nullable().default(null),
		executionId: IdSchema.nullable().default(null),
		nodeExecutionId: IdSchema.nullable().default(null),
		nodeAttemptId: IdSchema.nullable().default(null),
		taskProfile: TaskProfileSchema,
		/** Legacy text prompt. Ignored when `contents` is non-empty. */
		prompt: z.string().default(""),
		/** Multimodal parts. When set, takes precedence over `prompt`. */
		contents: z.array(ContentPartSchema).default([]),
		systemPrompt: z.string().nullable().default(null),
		maxOutputTokens: z.number().int().min(1).max(32000).default(2000),
		/** When set with modelId, skip routing and call this provider/model. */
		providerId: IdSchema.nullable().default(null),
		modelId: IdSchema.nullable().default(null),
		tools: InvokeProviderToolsSchema.default({ webSearch: false }),
		/**
		 * Set once a human has approved a cross-provider fallback. Without it a
		 * decision of FALLBACK_APPROVAL_REQUIRED stops the call (design §21.14).
		 */
		allowApprovedFallback: z.boolean().default(false),
		simulate: z
			.object({
				unavailableProviderKeys: z.array(z.string()).default([]),
				exhaustedAuthProfileIds: z.array(IdSchema).default([]),
				forceFailureClass: z.string().nullable().default(null),
			})
			.default({
				unavailableProviderKeys: [],
				exhaustedAuthProfileIds: [],
				forceFailureClass: null,
			}),
	})
	.superRefine((value, ctx) => {
		const hasContents = value.contents.length > 0;
		const hasPrompt = value.prompt.trim().length > 0;
		if (!hasContents && !hasPrompt) {
			ctx.addIssue({
				code: "custom",
				path: ["prompt"],
				message: "prompt or contents is required",
			});
		}
		if ((value.providerId == null) !== (value.modelId == null)) {
			ctx.addIssue({
				code: "custom",
				path: ["providerId"],
				message: "providerId and modelId must be set together for fixed invocation",
			});
		}
		for (const [index, part] of value.contents.entries()) {
			if (part.type === "image" && !part.artifactId && !part.url) {
				ctx.addIssue({
					code: "custom",
					path: ["contents", index],
					message: "image part requires artifactId or url",
				});
			}
		}
	});
export type InvokeProviderRequest = z.infer<typeof InvokeProviderRequestSchema>;

export const InvokeProviderResponseSchema = z.object({
	invocation: ProviderInvocationSchema,
	decision: RoutingDecisionSchema,
	output: z.string(),
});
export type InvokeProviderResponse = z.infer<typeof InvokeProviderResponseSchema>;

export const ProviderPageSchema = pageSchema(ProviderSchema);
export const ModelPageSchema = pageSchema(ModelSchema);
export const AuthenticationProfilePageSchema = pageSchema(AuthenticationProfileSchema);
export const ProviderAuthPolicyPageSchema = pageSchema(ProviderAuthPolicySchema);
export const SystemAuthPoolPageSchema = pageSchema(SystemAuthPoolSchema);
export const SystemAuthAssignmentPageSchema = pageSchema(SystemAuthAssignmentSchema);
export const ProviderInvocationPageSchema = pageSchema(ProviderInvocationSchema);
export const RoutingDecisionPageSchema = pageSchema(RoutingDecisionSchema);
